import { useState } from 'react';
import { Lock, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePersonStore } from '../../store/personStore';
import type { PersonDetail } from '../../types';

interface PersonPrivateNoteProps {
  person: PersonDetail;
  canEdit: boolean;
}

/**
 * Private rep note with inline editing. The backend only sends `personalNote`
 * to authorized viewers (owner / manager / admin), so this card is mounted only
 * when the viewer is authorized. The Edit button toggles an inline textarea that
 * saves just this field via PATCH /api/people/:id — the most-edited field on a
 * contact, so it gets a dedicated in-card editor rather than the full edit form.
 */
export function PersonPrivateNote({ person, canEdit }: PersonPrivateNoteProps) {
  const { updatePerson } = usePersonStore();
  const note = person.personalNote?.trim();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(person.personalNote ?? '');
  const [isSaving, setIsSaving] = useState(false);

  // Nothing to show and nobody here can add one → render nothing.
  if (!note && !canEdit) return null;

  const startEditing = () => {
    setDraft(person.personalNote ?? '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraft(person.personalNote ?? '');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updatePerson(person.id, { personalNote: draft.trim() || null });
      toast.success('Private note saved');
      setIsEditing(false);
    } catch {
      toast.error('Failed to save private note');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Private Notes
          </h3>
        </div>
        {canEdit && !isEditing && (
          <button
            type="button"
            onClick={startEditing}
            aria-label="Edit private notes"
            title="Edit private notes"
            className="btn-secondary text-sm py-1.5"
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            maxLength={5000}
            disabled={isSaving}
            autoFocus
            className="input disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="Private context — visible only to you and your manager."
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={cancelEditing}
              disabled={isSaving}
              className="btn-secondary text-sm py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary text-sm py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : note ? (
        <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
          {note}
        </p>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          No private notes yet. Use Edit to add one.
        </p>
      )}
    </div>
  );
}
